/* IL EDIT. Gift wrapping per item, inline (snippets/il-gift-wrap.liquid).
   Each wrapped line carries its own card as private properties; one Gift wrapping line carries
   the fee, and its quantity is kept equal to the number of wrapped lines. Cart and drawer
   rendering remain Prestige's. No private API access. */
(() => {
  'use strict';
  if (customElements.get('il-gift-wrap-state')) return;

  // Private keys. The underscore keeps them out of the customer's view at checkout and in their
  // emails; the admin order shows them under the item, which is where Jo reads them. Fixed words
  // rather than settings, because they are the order's record, not storefront copy, and the
  // packing slip template in reference/order-printer/ matches on them.
  const FLAG = '_Gift wrap';
  const KEYS = { to: '_To', from: '_From', message: '_Message' };
  const YES = 'Yes';
  const YES_NO_CARD = 'Yes, no card';

  // IL EDIT 26 October 2026: every card is copied into the order note as well (Ian), so the
  // order's Notes box holds all of them in one place. The item properties stay the record; the
  // note is rebuilt from them whenever the cart changes. Fixed words for the same reason as the
  // keys above. The block always sits at the end of the note, from its heading line on, so
  // anything else in the note is kept. The cart's own note box is switched off (Cart > Show
  // order note), so a shopper cannot type after it.
  const NOTE_HEADING = 'Gift wrapping';
  const NOTE_ROWS = { to: 'To', from: 'From', message: 'Message' };
  const NOTE_NO_CARD = 'No card';

  let pending = false;
  let failed = false;
  // A cart change that arrives while a correction is running is checked again afterwards.
  let again = false;
  // Whether the add buttons are currently locked, so Prestige's loading bar starts and ends once.
  let addsLocked = false;
  let nativePending = false;
  let nativeTimer;
  let checkoutPass = null;
  // Request and recovery limits are implementation safeguards, not merchant choices.
  const REQUEST_TIMEOUT = 15000;
  const route = path => `${window.Shopify.routes.root}${path}`;
  const stateNode = () => document.querySelector('il-gift-wrap-state');
  const state = () => ({ on: stateNode()?.dataset.on === 'true', variant: Number(stateNode()?.dataset.variant) || 0 });
  // Off/unavailable with no wrapped merchandise keeps Prestige's ordinary checkout path.
  const needsCheck = () => stateNode()?.dataset.check === 'true';

  // Prestige moves an open drawer to <body>, out of its section, and back on close. The cart's
  // section id is therefore remembered while the drawer is still in place, from page load on.
  let sectionId = '';
  const cartSection = () => {
    const found = stateNode()?.closest('.shopify-section')?.id.replace('shopify-section-', '');
    if (found) sectionId = found;
    return sectionId;
  };

  async function getCart() {
    const response = await fetch(route('cart.js'), { cache: 'no-store', signal: AbortSignal.timeout(REQUEST_TIMEOUT) });
    if (!response.ok) throw new Error('Could not read cart');
    return response.json();
  }

  async function post(path, body) {
    const response = await fetch(route(path), {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body), signal: AbortSignal.timeout(REQUEST_TIMEOUT)
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  }

  // A line's properties with its card replaced, or removed when values is null. Blank fields
  // leave no key, so the order shows only what was written.
  function withCard(properties, values) {
    const next = { ...properties };
    delete next[FLAG];
    for (const key of Object.values(KEYS)) delete next[key];
    if (!values) return next;
    const written = Object.entries(KEYS).filter(([name]) => values[name]);
    next[FLAG] = written.length ? YES : YES_NO_CARD;
    for (const [name, key] of written) next[key] = values[name];
    return next;
  }

  // Shopify cannot clear properties with an empty object. A blank private flag clears wrapping
  // in one change request, preserving the original quantity, selling plan and other properties.
  // Never add a replacement product: an interrupted add-then-remove can duplicate merchandise.
  async function writeCard(line, values) {
    const properties = withCard(line.properties || {}, values);
    if (!Object.keys(properties).length) properties[FLAG] = '';
    await post('cart/change.js', { id: line.key, quantity: line.quantity, properties });
  }

  // One fee per wrapped line. Returns true when the cart had to change.
  async function syncFee(cart) {
    const { variant } = state();
    if (!variant) return false;
    const wrapped = cart.items.filter(item => item.variant_id !== variant && item.properties?.[FLAG]).length;
    const fees = cart.items.filter(item => item.variant_id === variant);
    const charged = fees.reduce((sum, item) => sum + item.quantity, 0);
    if (fees.length <= 1 && charged === wrapped) return false;
    // Absolute quantities make recovery safe even when Shopify accepted a request but its
    // response was lost. A missing fee is addressed by variant; existing lines by their keys.
    const updates = Object.fromEntries(fees.map((fee, index) => [fee.key, index === 0 ? wrapped : 0]));
    if (!fees.length) updates[variant] = wrapped;
    await post('cart/update.js', { updates });
    return true;
  }

  // The note's gift wrapping block for this cart, one numbered entry per wrapped line.
  function wrapBlock(cart) {
    const { variant } = state();
    const lines = cart.items.filter(item => item.variant_id !== variant && item.properties?.[FLAG]);
    if (!lines.length) return '';
    const entries = lines.map((item, index) => {
      const rows = [`${index + 1}. ${item.title}${item.quantity > 1 ? ` x ${item.quantity}` : ''}`];
      for (const [name, key] of Object.entries(KEYS)) {
        if (item.properties[key]) rows.push(`${NOTE_ROWS[name]}: ${item.properties[key]}`);
      }
      if (rows.length === 1) rows.push(NOTE_NO_CARD);
      return rows.join('\n');
    });
    return [NOTE_HEADING, ...entries].join('\n\n');
  }

  // The note this cart should carry: whatever else it holds, then the block.
  function noteFor(cart) {
    const note = cart.note || '';
    const at = note.startsWith(`${NOTE_HEADING}\n`) ? 0 : note.indexOf(`\n${NOTE_HEADING}\n`);
    const rest = (at === -1 ? note : note.slice(0, at)).trimEnd();
    const block = wrapBlock(cart);
    return [rest, block].filter(Boolean).join('\n\n');
  }

  const noteSettled = cart => (cart.note || '') === noteFor(cart);

  async function syncNote(cart) {
    if (noteSettled(cart)) return;
    await post('cart/update.js', { note: noteFor(cart) });
  }

  // Whether the cart needs no correction: one fee line charging once per wrapped line, or, with
  // wrapping off, no wrapped lines at all. A cart with no wrapping and no fee is settled.
  function settled(cart) {
    const { on, variant } = state();
    const wrapped = cart.items.filter(item => item.variant_id !== variant && item.properties?.[FLAG]).length;
    if (!on) return wrapped === 0;
    const fees = cart.items.filter(item => item.variant_id === variant);
    return fees.length <= 1 && fees.reduce((n, item) => n + item.quantity, 0) === wrapped;
  }

  async function verifyFee() {
    let changed = false;
    // Always re-read before retrying. Never replay an add or a stale line key.
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const cart = await getCart();
        if (!state().on) {
          if (cart.items.some(item => item.properties?.[FLAG])) throw new Error('Wrapping is unavailable');
          await syncNote(cart);
          return changed;
        }
        changed = await syncFee(cart) || changed;
        // The note changes no total, so it does not count as a change the shopper must see.
        await syncNote(cart);
        const verified = await getCart();
        if (settled(verified) && noteSettled(verified)) return changed;
        throw new Error('Wrapping fee did not match');
      } catch (error) {
        // An ambiguous write may have succeeded; refresh the rendered totals after recovery.
        changed = true;
        if (attempt === 1) throw error;
      }
    }
  }

  function paintGuard() {
    const locked = pending || nativePending || failed;
    for (const node of document.querySelectorAll('[data-il-gw-guard]')) {
      node.hidden = !locked;
      const status = node.querySelector('[data-il-gw-status]');
      const message = failed ? node.dataset.error : node.dataset.pending;
      if (status.textContent !== message) status.textContent = message;
      node.querySelector('[data-il-gw-retry]').hidden = !failed;
    }
    for (const fieldset of document.querySelectorAll('[data-il-gw-checkout]')) fieldset.disabled = locked;
    for (const button of document.querySelectorAll('button[name="checkout"], input[name="checkout"]')) {
      if (locked && !button.disabled) { button.dataset.ilGwDisabled = 'true'; button.disabled = true; }
      else if (!locked && button.dataset.ilGwDisabled) { button.disabled = false; delete button.dataset.ilGwDisabled; }
    }
    lockAdds();
  }

  // IL EDIT 25 October 2026: while a fee correction (or a card save, or checkout's own check) holds
  // the lock, every Add to cart is visibly unavailable (dimmed, marked aria-disabled), with
  // Prestige's loading bar running, as for any add in progress. That covers the product page buttons, the
  // sticky bar, the drawer suggestions and any button drawn while the lock is held (the observer
  // below calls this after every replacement). The lock never touches disabled or aria-busy, which
  // belong to Prestige (sold out, add in progress): it adds its own data-il-gw-lock and aria-disabled,
  // shown by il-custom.css, and the capture listener below refuses the click, so nothing of
  // Prestige's is saved or restored. Never on without a lock:
  // the lock is taken only when the fee needs fixing, after a read that blocks
  // nothing, and every path that takes it clears it in a finally, with each request timed out.
  const addTarget = 'product-form button[type="submit"], buy-buttons button[type="submit"]';
  function lockAdds() {
    let restored = false;
    for (const button of document.querySelectorAll(addTarget)) {
      const marked = button.hasAttribute('data-il-gw-lock');
      if (pending && !marked) {
        button.setAttribute('data-il-gw-lock', '');
        button.setAttribute('aria-disabled', 'true');
      } else if (!pending && marked) {
        button.removeAttribute('data-il-gw-lock');
        button.removeAttribute('aria-disabled');
        restored = true;
      }
    }
    if (pending !== addsLocked) {
      addsLocked = pending;
      document.documentElement.dispatchEvent(new CustomEvent(`theme:loading:${pending ? 'start' : 'end'}`, { bubbles: true }));
    }
    // A label change asked for while a button was disabled was skipped by label(); catch it up.
    if (restored) for (const box of productBoxes()) label(box);
  }

  // The cart page reloads, so its Liquid totals are right. Elsewhere the drawer and the cart
  // count take Prestige's own cart:change, with the drawer section bundled as Prestige does.
  async function refresh() {
    if (window.location.pathname === route('cart')) { window.location.reload(); return; }
    const cart = await getCart();
    const section = cartSection();
    if (!section) { document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true })); return; }
    const url = new URL(route(''), window.location.origin);
    url.searchParams.set('sections', section);
    const response = await fetch(url, { cache: 'no-store', signal: AbortSignal.timeout(REQUEST_TIMEOUT) });
    if (!response.ok) throw new Error('Could not refresh cart');
    cart.sections = await response.json();
    if (!cart.sections[section]) throw new Error('Cart section was unavailable');
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'il-gift-wrap', cart } }));
  }

  // Where the browser cannot size a textarea to its content, the cart editor's message grows here.
  const autosize = !CSS.supports('field-sizing', 'content');

  // Character counter and card preview, shared by the product page box and the cart editor.
  function paint(root) {
    const d = root.closest('[data-limit]')?.dataset || {};
    const get = name => root.querySelector(`[data-il-gw-${name}]`);
    const message = get('message');
    if (get('count') && message) {
      get('count').textContent = (d.countPattern || '').replaceAll('[used]', message.value.length).replaceAll('[limit]', d.limit);
    }
    if (autosize && message && root.matches('[data-il-gw-editor]')) {
      message.style.height = 'auto';
      message.style.height = `${message.scrollHeight + 2}px`;
    }
    if (!get('card')) return;
    const to = get('to').value.trim();
    const from = get('from').value.trim();
    const text = message.value.trim();
    get('card').hidden = !(to || from || text);
    // The "leave all three blank" hint is about a blank card, so it goes once anything is written.
    if (get('hint')) get('hint').hidden = Boolean(to || from || text);
    get('card-to').textContent = to ? (d.greeting || '').replaceAll('[to]', to) : '';
    get('card-msg').textContent = text;
    get('card-sign').textContent = from ? (d.signoff || '').replaceAll('[from]', from) : '';
  }

  /* ---------- Product page ---------- */

  const productBoxes = () => document.querySelectorAll('[data-il-gw-product]');
  const submitButton = box => document.querySelector(`buy-buttons[form="${CSS.escape(box.dataset.form)}"] button[type="submit"]`);

  // The Add to cart label says so while the switch is on. Prestige replaces <buy-buttons> on a
  // variant change, so this is applied again after every one. Prestige's sticky bar submits the
  // same form, so its button says so too.
  function label(box) {
    const on = box.querySelector('[data-il-gw-switch]').checked;
    for (const button of document.querySelectorAll(`buy-buttons[form="${CSS.escape(box.dataset.form)}"] button[type="submit"]`)) {
      if (button.disabled) continue;
      if (on && box.dataset.addLabel) {
        if (!('ilGwOriginal' in button.dataset)) button.dataset.ilGwOriginal = button.textContent;
        if (button.textContent !== box.dataset.addLabel) button.textContent = box.dataset.addLabel;
      } else if ('ilGwOriginal' in button.dataset) {
        button.textContent = button.dataset.ilGwOriginal;
        delete button.dataset.ilGwOriginal;
      }
    }
  }

  // Keep Add to cart on screen (Theme settings > IL | Gift wrap). An open card can push Add to
  // cart below the screen, and Prestige's sticky bar only shows once the button has scrolled
  // past, at the top. So while the switch is on and the button is below the screen, the same bar
  // shows at the foot of the screen, and goes as soon as the button is in view.
  const stickyBar = box => document.querySelector(`product-sticky-bar[form="${CSS.escape(box.dataset.form)}"]`);
  // Implementation constant: a little over Prestige's 0.2s fade, so the bar never slides between
  // the top and foot of the screen while it can still be seen.
  const FADE = 250;
  const fades = new WeakMap();
  const watching = new Set();
  let ticking = false;
  const below = box => {
    const button = submitButton(box);
    return Boolean(button) && button.getBoundingClientRect().top >= window.innerHeight;
  };
  const checkAll = () => { ticking = false; for (const box of watching) follow(box, below(box)); };
  const onMove = () => { if (!ticking) { ticking = true; requestAnimationFrame(checkAll); } };
  // Scrolling and resizing are listened to directly, because a jump (Home, back to top) can carry
  // the button from above the screen to below it without the observer seeing it cross. The
  // observer catches the rest: a card growing as it is typed moves the button without a scroll.
  const watcher = new IntersectionObserver(onMove);

  function follow(box, show) {
    const bar = stickyBar(box);
    if (!bar) return;
    clearTimeout(fades.get(bar));
    if (show) {
      // Prestige's own observer can miss the same jump and leave its scrolled-past state on. With
      // the button below the screen that state is wrong by Prestige's own rule (the form's bottom
      // above the screen), and Prestige sets it again the next time the form scrolls past.
      bar.classList.remove('is-visible');
      bar.classList.add('il-gw-foot', 'il-gw-follow');
      label(box);
    } else if (bar.classList.contains('il-gw-follow')) {
      bar.classList.remove('il-gw-follow');
      fades.set(bar, setTimeout(() => bar.classList.remove('il-gw-foot'), FADE));
    }
  }

  function watch(box) {
    const on = box.dataset.follow === 'true' && box.querySelector('[data-il-gw-switch]').checked;
    if (on) watching.add(box);
    else watching.delete(box);
    // Re-observed from scratch, because a variant change replaces the button.
    watcher.disconnect();
    for (const watched of watching) {
      const button = submitButton(watched);
      if (button) watcher.observe(button);
    }
    if (watching.size) {
      window.addEventListener('scroll', onMove, { passive: true });
      window.addEventListener('resize', onMove);
    } else {
      window.removeEventListener('scroll', onMove);
      window.removeEventListener('resize', onMove);
    }
    follow(box, on && below(box));
  }

  function toggle(box) {
    const on = box.querySelector('[data-il-gw-switch]').checked;
    const fields = box.querySelector('[data-il-gw-fields]');
    fields.hidden = !on;
    // Disabled fields do not post, so a switched off box adds nothing to the product.
    for (const input of fields.querySelectorAll('input, textarea')) input.disabled = !on;
    box.classList.toggle('is-on', on);
    // Accelerated product checkout cannot include the separate wrapping fee.
    const buttons = document.querySelector(`buy-buttons[form="${CSS.escape(box.dataset.form)}"]`);
    for (const payment of buttons?.querySelectorAll('.shopify-payment-button') || []) payment.hidden = on;
    paint(box);
    label(box);
    watch(box);
  }

  function resetBox(box) {
    const toggleInput = box.querySelector('[data-il-gw-switch]');
    if (!toggleInput.checked) return;
    toggleInput.checked = false;
    for (const input of box.querySelectorAll('[data-il-gw-fields] input, [data-il-gw-fields] textarea')) input.value = '';
    toggle(box);
  }

  /* ---------- Cart rows ---------- */

  const itemField = (item, name) => item.querySelector(`[data-il-gw-${name}]`);

  function busy(item, on) {
    pending = on;
    paintGuard();
    item.setAttribute('aria-busy', String(on));
    for (const button of item.querySelectorAll('button')) button.disabled = on;
  }

  function fail(item, error) {
    console.warn('il-gift-wrap:', error);
    const box = itemField(item, 'error');
    box.textContent = item.dataset.error;
    box.hidden = false;
    box.focus();
  }

  function openEditor(item) {
    for (const other of document.querySelectorAll('[data-il-gw-item] [data-il-gw-editor]:not([hidden])')) {
      if (!item.contains(other)) closeEditor(other.closest('[data-il-gw-item]'));
    }
    itemField(item, 'to').value = item.dataset.to || '';
    itemField(item, 'from').value = item.dataset.from || '';
    itemField(item, 'message').value = item.dataset.message || '';
    itemField(item, 'error').hidden = true;

    // "Same card as" each other wrapped item, for one card across several presents.
    const same = itemField(item, 'same');
    same.replaceChildren();
    // The cart page carries the drawer's rows as well as its own, so each line is offered once.
    const offered = new Set([item.dataset.key]);
    for (const other of document.querySelectorAll('[data-il-gw-item][data-wrapped="true"]')) {
      if (offered.has(other.dataset.key) || !(other.dataset.to || other.dataset.from || other.dataset.message)) continue;
      offered.add(other.dataset.key);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'il-gw-item__act';
      button.textContent = (item.dataset.same || '').replaceAll('[item]', other.dataset.title);
      button.dataset.ilGwCopy = other.dataset.key;
      same.append(button);
    }
    same.hidden = !same.childElementCount;

    itemField(item, 'summary').hidden = true;
    itemField(item, 'editor').hidden = false;
    for (const opener of item.querySelectorAll('[data-il-gw-edit]')) opener.setAttribute('aria-expanded', 'true');
    paint(itemField(item, 'editor'));
    itemField(item, 'to').focus();
  }

  function closeEditor(item) {
    if (!item) return;
    itemField(item, 'editor').hidden = true;
    itemField(item, 'summary').hidden = false;
    for (const opener of item.querySelectorAll('[data-il-gw-edit]')) opener.setAttribute('aria-expanded', 'false');
    item.querySelector('[data-il-gw-summary] [data-il-gw-edit], [data-il-gw-summary][data-il-gw-edit]')?.focus();
  }

  function copyCard(item, fromKey) {
    const source = [...document.querySelectorAll('[data-il-gw-item]')].find(other => other.dataset.key === fromKey);
    if (!source) return;
    itemField(item, 'to').value = source.dataset.to || '';
    itemField(item, 'from').value = source.dataset.from || '';
    itemField(item, 'message').value = source.dataset.message || '';
    paint(itemField(item, 'editor'));
  }

  async function saveItem(item, remove = false) {
    if (pending || nativePending) return;
    const values = remove ? null : {
      to: itemField(item, 'to').value.trim(),
      from: itemField(item, 'from').value.trim(),
      message: itemField(item, 'message').value.trim()
    };
    if (values && values.message.length > Number(item.dataset.limit)) { fail(item, new Error('Message too long')); return; }
    failed = false;
    busy(item, true);
    try {
      const cart = await getCart();
      const line = cart.items.find(entry => entry.key === item.dataset.key);
      if (!line) throw new Error('Line no longer in the cart');
      await writeCard(line, values);
      await verifyFee();
      await refresh();
      busy(item, false);
    } catch (error) {
      failed = true;
      busy(item, false);
      // Removal failures need the same visible recovery as an edit. A lost response is not
      // proof the change failed, so retry re-reads and renders the server's current basket.
      if (!remove) fail(item, error);
      else console.warn('il-gift-wrap:', error);
    }
  }

  /* ---------- Keeping the fee right ---------- */

  // Runs whenever the cart renders: after a product page add, after a line is removed, after
  // Prestige refreshes the drawer. A wrapped line removed takes its fee with it.
  // IL EDIT 25 October 2026: the cart is read first without the lock, and the lock (checkout and
  // the cart's own buttons held, see below) is taken only when the fee has to be corrected. Most
  // carts hold no wrapping at all, and before this every page load and every cart change locked
  // Add to cart for two cart reads. A read changes nothing, so it cannot race; the correction
  // re-reads under the lock before it writes.
  async function reconcile() {
    cartSection();
    if (!needsCheck() || nativePending) return;
    if (pending) { again = true; return; }
    let cart = null;
    try { cart = await getCart(); } catch (error) { /* The locked check below retries and reports. */ }
    if (nativePending) return;
    if (pending) { again = true; return; }
    if (cart && settled(cart) && noteSettled(cart)) {
      if (failed) { failed = false; paintGuard(); }
      return;
    }
    pending = true;
    failed = false;
    paintGuard();
    try {
      const changed = await verifyFee();
      if (changed) await refresh();
    } catch (error) {
      failed = true;
      console.warn('il-gift-wrap:', error);
    } finally {
      pending = false;
      paintGuard();
      if (again) { again = false; reconcile(); }
    }
  }

  /* ---------- Events ---------- */

  // Prestige announces its cart writes before starting the request. Keep checkout locked until
  // its replacement cart arrives. A bounded timeout offers visible recovery after a network loss.
  document.addEventListener('cart:prepare-bundled-sections', () => {
    if (!needsCheck()) return;
    nativePending = true;
    clearTimeout(nativeTimer);
    nativeTimer = setTimeout(() => { nativePending = false; failed = true; paintGuard(); }, REQUEST_TIMEOUT * 2);
    paintGuard();
  });
  document.addEventListener('cart:change', event => {
    if (event.detail?.baseEvent === 'il-gift-wrap') return;
    nativePending = false;
    clearTimeout(nativeTimer);
    reconcile();
  });
  document.addEventListener('cart:error', () => {
    nativePending = false;
    clearTimeout(nativeTimer);
    reconcile();
  });

  // Replaced drawer buttons must inherit the current lock, and so must replaced Add to cart buttons
  // (a variant change, a redrawn drawer suggestion), through lockAdds(). Observe child replacement only;
  // observing our own disabled/hidden attributes would create a feedback loop.
  new MutationObserver(paintGuard).observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('submit', async event => {
    if (event.submitter?.name !== 'checkout' || !needsCheck()) return;
    if (checkoutPass === event.target) { checkoutPass = null; return; }
    event.preventDefault();
    event.stopImmediatePropagation();
    if (pending || nativePending || failed) { paintGuard(); return; }
    const form = event.target, button = event.submitter;
    pending = true;
    paintGuard();
    try {
      const changed = await verifyFee();
      // A corrected charge needs to be visible before the shopper accepts the total.
      if (changed) { await refresh(); return; }
      pending = false;
      paintGuard();
      if (form.isConnected && button.isConnected) { checkoutPass = form; form.requestSubmit(button); }
    } catch (error) { failed = true; console.warn('il-gift-wrap:', error); }
    finally { pending = false; paintGuard(); }
  }, true);

  // Do not let a quantity/remove/add request race a wrapping update or checkout verification.
  // IL EDIT 25 October 2026: a backstop only. Add to cart is disabled and shown busy for the whole
  // lock (lockAdds above), so this catches just a button drawn in the moment before the observer
  // reaches it, or an Enter key in the form; such a button is locked here first, so it shows busy
  // and nothing is refused without a sign. The cart's own quantity and remove controls are refused
  // while locked: the correction may redraw their row, and the cart shows its pending notice.
  const mutationTarget = 'line-item-quantity a, line-item-quantity button, line-item-quantity input';
  for (const type of ['click', 'change', 'submit']) document.addEventListener(type, event => {
    if (!pending) return;
    const target = event.target;
    const add = target.closest(addTarget) || (type === 'submit' && target.closest('product-form'));
    if (target.closest(mutationTarget) || add) {
      event.preventDefault(); event.stopImmediatePropagation();
      if (add) lockAdds();
    }
  }, true);

  document.addEventListener('click', event => {
    const target = event.target;
    if (target.closest('[data-il-gw-retry]')) {
      if (pending || nativePending) return;
      // Reload the authoritative server basket, including a possibly saved card, after repair.
      (async () => { pending = true; failed = false; paintGuard();
        try { await verifyFee(); await refresh(); }
        catch (error) { failed = true; console.warn('il-gift-wrap:', error); }
        finally { pending = false; paintGuard(); }
      })();
      return;
    }
    const item = target.closest('[data-il-gw-item]');
    if (!item) return;
    if (target.closest('[data-il-gw-edit]')) openEditor(item);
    else if (target.closest('[data-il-gw-cancel]')) closeEditor(item);
    else if (target.closest('[data-il-gw-save]')) saveItem(item);
    else if (target.closest('[data-il-gw-remove]')) saveItem(item, true);
    else if (target.closest('[data-il-gw-copy]')) copyCard(item, target.closest('[data-il-gw-copy]').dataset.ilGwCopy);
  });

  document.addEventListener('change', event => {
    if (event.target.matches('[data-il-gw-switch]')) toggle(event.target.closest('[data-il-gw-product]'));
  });

  document.addEventListener('input', event => {
    const root = event.target.closest('[data-il-gw-fields], [data-il-gw-editor]');
    if (root) paint(root);
  });

  // Before Prestige reads the form: trim the card and mark a wrap with no card as such.
  document.addEventListener('submit', event => {
    for (const box of productBoxes()) {
      // getAttribute, because a form's .id is its field named "id", the variant input.
      if (event.target.getAttribute('id') !== box.dataset.form) continue;
      const toggleInput = box.querySelector('[data-il-gw-switch]');
      if (!toggleInput.checked) continue;
      const fields = [...box.querySelectorAll('[data-il-gw-fields] input, [data-il-gw-fields] textarea')];
      for (const field of fields) field.value = field.value.trim();
      toggleInput.value = fields.some(field => field.value) ? YES : YES_NO_CARD;
      // An empty field would post as an empty key, a blank "_To:" line on Jo's order. Prestige
      // reads the form in this same event, so empty fields sit out for it and come straight back.
      const empty = fields.filter(field => !field.value);
      for (const field of empty) field.disabled = true;
      setTimeout(() => { if (toggleInput.checked) for (const field of empty) field.disabled = false; });
    }
  }, true);

  // Added: switch the box off again, so a second click on Add to cart is not wrapped by accident.
  document.addEventListener('variant:add', event => {
    for (const box of productBoxes()) if (event.target.getAttribute('id') === box.dataset.form) resetBox(box);
  });

  document.addEventListener('variant:change', () => { for (const box of productBoxes()) toggle(box); });

  customElements.define('il-gift-wrap-state', class extends HTMLElement {
    connectedCallback() { cartSection(); queueMicrotask(() => { if (this.isConnected) reconcile(); }); }
  });
})();
